import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MIN_SCROLL_HEIGHT = 120;
const MIN_SCROLL_WIDTH = 200;
const MIN_THUMB_HEIGHT = 40;
const MIN_OVERLAY_SCROLL_HEIGHT = 44;
const OVERLAY_LIST_SELECTOR = '[role="listbox"], .MuiMenu-list, .MuiAutocomplete-listbox';
const OVERLAY_PAPER_SELECTOR = '.MuiMenu-paper, .MuiAutocomplete-paper, .MuiPopover-paper';

const isScrollableModalRegion = (element) => {
  if (!(element instanceof HTMLElement)) return false;
  if (element.closest('.olcPersistentModalScrollbarTrack')) return false;
  if (element.closest('.olcNoPersistentScrollbar')) return false;
  if (element.classList.contains('olcNativeScrollbar')) return false;
  if (element.closest('.createOrderDropdownMenu')) return false;
  if (element.matches('input, textarea')) return false;

  const isOverlayList =
    element.matches(OVERLAY_LIST_SELECTOR) && Boolean(element.closest(OVERLAY_PAPER_SELECTOR));
  if (element.matches(OVERLAY_LIST_SELECTOR) && !isOverlayList) return false;

  const styles = window.getComputedStyle(element);
  if (!['auto', 'scroll'].includes(styles.overflowY)) return false;
  if (
    element.clientHeight < (isOverlayList ? MIN_OVERLAY_SCROLL_HEIGHT : MIN_SCROLL_HEIGHT) ||
    (!isOverlayList && element.clientWidth < MIN_SCROLL_WIDTH)
  ) {
    return false;
  }

  return element.scrollHeight - element.clientHeight > 1;
};

const PersistentModalScrollbars = () => {
  const [scrollbars, setScrollbars] = useState([]);
  const activeTargetsRef = useRef(new Set());
  const dragStateRef = useRef(null);
  const elementIdsRef = useRef(new WeakMap());
  const nextElementIdRef = useRef(1);

  useEffect(() => {
    let animationFrameId = null;
    const observedElements = new Set();

    const scheduleUpdate = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(updateScrollbars);
    };

    const resizeObserver = new ResizeObserver(scheduleUpdate);

    const updateScrollbars = () => {
      animationFrameId = null;
      const modalRoots = Array.from(document.querySelectorAll('.MuiModal-root, .MuiPopper-root'));
      const nextTargets = new Set();
      const nextObservedElements = new Set(modalRoots);
      const nextScrollbars = [];

      modalRoots.forEach((modalRoot) => {
        const candidates = Array.from(modalRoot.querySelectorAll('*')).filter(
          isScrollableModalRegion
        );
        const innermostCandidates = candidates.filter(
          (candidate) =>
            !candidates.some(
              (otherCandidate) => otherCandidate !== candidate && candidate.contains(otherCandidate)
            )
        );

        innermostCandidates.forEach((target) => {
          if (nextTargets.has(target)) return;

          const targetRect = target.getBoundingClientRect();
          const dialogPaper = target.closest('.MuiDialog-paper');
          const overlayPaper = target.closest(OVERLAY_PAPER_SELECTOR);
          const shellRect = (dialogPaper || overlayPaper)?.getBoundingClientRect();
          const isOverlayScrollbar = Boolean(overlayPaper && !dialogPaper);
          if (
            targetRect.width <= 0 ||
            targetRect.height <= 0 ||
            targetRect.bottom <= 0 ||
            targetRect.top >= window.innerHeight
          ) {
            return;
          }

          let id = elementIdsRef.current.get(target);
          if (!id) {
            id = nextElementIdRef.current;
            nextElementIdRef.current += 1;
            elementIdsRef.current.set(target, id);
          }

          const maxScrollTop = Math.max(target.scrollHeight - target.clientHeight, 0);
          const thumbHeight = Math.max(
            MIN_THUMB_HEIGHT,
            Math.round((target.clientHeight / target.scrollHeight) * target.clientHeight)
          );
          const maxThumbTop = Math.max(target.clientHeight - thumbHeight, 0);
          const thumbTop = maxScrollTop
            ? Math.round((target.scrollTop / maxScrollTop) * maxThumbTop)
            : 0;

          nextTargets.add(target);
          nextObservedElements.add(target);
          nextScrollbars.push({
            id,
            // Keep the rail out of form and option content. Reusable dialogs
            // and dropdown overlays both place it on their shell edge.
            left: isOverlayScrollbar
              ? overlayPaper.clientWidth
              : shellRect?.right ?? targetRect.right,
            // MUI's Grow transition is applied to the dropdown paper itself.
            // Keep the overlay rail inside that paper so it fades with the
            // options instead of briefly remaining on screen by itself.
            portalContainer: isOverlayScrollbar
              ? overlayPaper
              : dialogPaper?.parentElement ?? modalRoot,
            position: isOverlayScrollbar ? 'absolute' : 'fixed',
            target,
            thumbHeight,
            thumbTop,
            top: isOverlayScrollbar ? targetRect.top - shellRect.top : targetRect.top,
            trackHeight: target.clientHeight,
          });
        });
      });

      activeTargetsRef.current.forEach((target) => {
        if (!nextTargets.has(target)) {
          target.classList.remove('olcPersistentScrollbarTarget');
        }
      });
      nextTargets.forEach((target) => target.classList.add('olcPersistentScrollbarTarget'));
      activeTargetsRef.current = nextTargets;

      observedElements.forEach((element) => {
        if (!nextObservedElements.has(element)) {
          resizeObserver.unobserve(element);
          observedElements.delete(element);
        }
      });
      nextObservedElements.forEach((element) => {
        if (!observedElements.has(element)) {
          observedElements.add(element);
          resizeObserver.observe(element);
        }
      });

      setScrollbars(nextScrollbars);
    };

    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    document.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      document.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
      activeTargetsRef.current.forEach((target) => {
        target.classList.remove('olcPersistentScrollbarTarget');
      });
      activeTargetsRef.current.clear();
    };
  }, []);

  const handleTrackPointerDown = (event, scrollbar) => {
    event.preventDefault();
    event.stopPropagation();

    const trackRect = event.currentTarget.getBoundingClientRect();
    const maxThumbTop = scrollbar.trackHeight - scrollbar.thumbHeight;
    const requestedThumbTop = Math.min(
      Math.max(event.clientY - trackRect.top - scrollbar.thumbHeight / 2, 0),
      maxThumbTop
    );
    const maxScrollTop = scrollbar.target.scrollHeight - scrollbar.target.clientHeight;
    scrollbar.target.scrollTop = maxThumbTop ? (requestedThumbTop / maxThumbTop) * maxScrollTop : 0;
  };

  const handleThumbPointerDown = (event, scrollbar) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      pointerId: event.pointerId,
      scrollbar,
      startClientY: event.clientY,
      startScrollTop: scrollbar.target.scrollTop,
    };
  };

  const handleThumbPointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const { scrollbar } = dragState;
    const maxThumbTop = scrollbar.trackHeight - scrollbar.thumbHeight;
    const maxScrollTop = scrollbar.target.scrollHeight - scrollbar.target.clientHeight;
    if (maxThumbTop <= 0 || maxScrollTop <= 0) return;

    const pointerDelta = event.clientY - dragState.startClientY;
    scrollbar.target.scrollTop =
      dragState.startScrollTop + (pointerDelta / maxThumbTop) * maxScrollTop;
  };

  const handleThumbPointerUp = (event) => {
    if (dragStateRef.current?.pointerId !== event.pointerId) return;

    dragStateRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return scrollbars.map((scrollbar) =>
    createPortal(
      <div
        aria-hidden="true"
        className="olcPersistentModalScrollbarTrack"
        data-testid="olc-persistent-modal-scrollbar"
        key={scrollbar.id}
        onPointerDown={(event) => handleTrackPointerDown(event, scrollbar)}
        style={{
          height: `${scrollbar.trackHeight}px`,
          left: `${scrollbar.left}px`,
          position: scrollbar.position,
          top: `${scrollbar.top}px`,
        }}
      >
        <div
          className="olcPersistentModalScrollbarThumb"
          onPointerCancel={handleThumbPointerUp}
          onPointerDown={(event) => handleThumbPointerDown(event, scrollbar)}
          onPointerMove={handleThumbPointerMove}
          onPointerUp={handleThumbPointerUp}
          style={{
            height: `${scrollbar.thumbHeight}px`,
            transform: `translateY(${scrollbar.thumbTop}px)`,
          }}
        />
      </div>,
      scrollbar.portalContainer
    )
  );
};

export default PersistentModalScrollbars;
